/**
 * ZEN ERP — Paylaşılan teklif/sipariş PDF linkinin açılış kayıtları
 * QuoteBuilderPage.jsx'ten çıkarıldı.
 */
import React, { useState, useEffect } from "react";
import { X, MapPin } from "lucide-react";
import api from "../api";

function uaLabel(ua = "") {
  const u = ua.toLowerCase();
  const dev = /iphone/.test(u) ? "iPhone" : /ipad/.test(u) ? "iPad" : /android/.test(u) ? "Android" :
    /windows/.test(u) ? "Windows" : /mac os/.test(u) ? "Mac" : /linux/.test(u) ? "Linux" : "Bilinmiyor";
  const br = /edg\//.test(u) ? "Edge" : /opr\//.test(u) ? "Opera" : /chrome/.test(u) ? "Chrome" :
    /safari/.test(u) ? "Safari" : /firefox/.test(u) ? "Firefox" : "";
  return br ? `${dev} · ${br}` : dev;
}

export function QuoteViewsModal({ refType = "quote", refId, title, subtitle, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/share-views/detail", { params: { refType, refId } })
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]));
  }, [refType, refId]);

  const human = (rows || []).filter((r) => !r.isBot);
  const bots = (rows || []).filter((r) => r.isBot);
  const dt = (v) => new Date(v).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const place = (g) => {
    if (!g) return "";
    const near = [g.district, g.city].filter(Boolean).join(" / ");
    return [near, g.zip, g.region !== g.city ? g.region : null, g.country].filter(Boolean).join(", ");
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="quote-views-modal">
      <div className="w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col rounded-xl text-black shadow-2xl zen-anim-up" style={{ colorScheme: "light", background: "white" }}>
        <div className="px-6 py-4 border-b-2 border-black flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold leading-tight text-black">{title} — Açılış Kayıtları</h3>
            <p className="text-sm text-neutral-600 mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose} data-testid="quote-views-close-btn" className="p-2 rounded hover:bg-neutral-200 transition-colors">
            <X className="w-5 h-5 text-black" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-6" style={{ background: "white" }}>
          {rows === null ? (
            <div className="text-sm text-neutral-600 py-6 text-center">Yükleniyor...</div>
          ) : (
            <>
              <div>
                <div className="text-sm font-bold text-black mb-2 uppercase tracking-wide">
                  Müşteri Açılışları ({human.length})
                </div>
                {human.length === 0 ? (
                  <div className="border border-dashed border-neutral-400 p-6 text-center text-sm text-neutral-600">
                    Bu belge henüz müşteri tarafından açılmadı.
                  </div>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-black text-xs uppercase tracking-wide text-neutral-700">
                        <th className="text-left py-2 pr-2 w-8">#</th>
                        <th className="text-left py-2 pr-3">Tarih / Saat</th>
                        <th className="text-left py-2 pr-3">Konum</th>
                        <th className="text-left py-2">Cihaz / Operatör / IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {human.map((r, i) => (
                        <tr key={r.id || i} className="border-b border-neutral-300 align-top" data-testid={`quote-view-row-${i}`}>
                          <td className="py-2.5 pr-2 font-bold text-black">{human.length - i}</td>
                          <td className="py-2.5 pr-3 font-semibold text-black whitespace-nowrap">{dt(r.at)}</td>
                          <td className="py-2.5 pr-3 text-black">
                            {r.gps ? (
                              <>
                                <span className="font-semibold">{r.gps.address || "Kesin konum"}</span>{" "}
                                <a
                                  href={`https://www.google.com/maps?q=${r.gps.lat},${r.gps.lon}`}
                                  target="_blank" rel="noreferrer"
                                  className="inline-flex items-center gap-0.5 underline text-blue-700 whitespace-nowrap"
                                  data-testid={`quote-view-map-${i}`}
                                ><MapPin className="w-3 h-3" /> Haritada Gör</a>
                                <div className="text-xs text-neutral-600 zen-mono">
                                  {r.gps.lat.toFixed(6)}, {r.gps.lon.toFixed(6)}
                                  {r.gps.accuracy ? ` · GPS ±${Math.round(r.gps.accuracy)} m` : " · GPS"}
                                </div>
                              </>
                            ) : (
                              <>
                                {place(r.geo) || "Bilinmiyor"}
                                {r.geo?.lat != null && (
                                  <>
                                    {" "}
                                    <a
                                      href={`https://www.google.com/maps?q=${r.geo.lat},${r.geo.lon}`}
                                      target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-0.5 underline text-blue-700 whitespace-nowrap"
                                      data-testid={`quote-view-map-${i}`}
                                    ><MapPin className="w-3 h-3" /> Haritada Gör</a>
                                  </>
                                )}
                                <div className="text-xs text-neutral-500">
                                  yaklaşık (IP){r.geo?.mobile ? " · mobil veri" : ""}
                                  {r.geoError && (
                                    <> · konum {r.geoError.code === 1 ? "izni verilmedi" : r.geoError.code === 3 ? "zaman aşımına uğradı" : "alınamadı"}</>
                                  )}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="py-2.5 text-neutral-700 text-xs leading-relaxed">
                            {uaLabel(r.ua)}<br />
                            {r.geo?.isp || "-"}<br />
                            <span className="zen-mono">IP {r.ip || "-"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {bots.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-neutral-700 mb-2 uppercase tracking-wide">
                    Otomatik Önizlemeler ({bots.length})
                  </div>
                  <div className="border border-neutral-300">
                    {bots.map((r, i) => (
                      <div key={r.id || i} className="flex items-center justify-between gap-4 px-3 py-1.5 text-xs text-neutral-600 border-b border-neutral-200 last:border-b-0">
                        <span className="zen-mono shrink-0">{dt(r.at)}</span>
                        <span className="truncate">{r.geo?.isp || (r.ua || "-").slice(0, 40)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-neutral-300 pt-3 text-xs text-neutral-600 leading-relaxed">
                Link açıldığında müşteriye "Belgeyi Görüntüle" sayfası çıkar ve tarayıcı konum izni ister. İzin verilirse
                <b className="text-black"> GPS hassasiyetinde (±10-50 m) kesin konum</b> ve açık adres kaydedilir; izin verilmezse
                yalnızca IP'ye göre yaklaşık konum (operatör santrali, 5-50 km sapma) görünür. WhatsApp/Facebook'un otomatik
                önizleme istekleri <b className="text-black">Otomatik Önizlemeler</b> altında toplanır, görüldü sayısına eklenmez.
                Telefon numarası teknik olarak elde edilemez.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
